import { useEffect, useState, useCallback } from 'react'
import { usePartnerAuth } from '../context/PartnerAuthContext'
import { supabase } from '../lib/supabaseClient'
import type {
  PartnerDashboardStats,
  ActiveCoupon,
  RecentOrder,
  PartnerDashboardData,
} from '../types/partnerDashboard'

/**
 * Retourne le premier et dernier jour du mois courant (format YYYY-MM-DD).
 */
function currentMonthRange(): { fromDate: string; toDate: string } {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  const fromDate = `${y}-${String(m + 1).padStart(2, '0')}-01`
  const lastDay = new Date(y, m + 1, 0).getDate()
  const toDate = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
  return { fromDate, toDate }
}

/**
 * Hook centralisant les données du dashboard partenaire.
 *
 * Lance en parallèle :
 *   a) stats commandes du mois (via RPC partner_dashboard_stats)
 *   b) coupon actif du partenaire
 *   c) 10 dernières commandes
 *
 * Sécurité : filtre toujours par partner_id issu du contexte auth.
 */
export function usePartnerDashboardData(): PartnerDashboardData {
  const { partner } = usePartnerAuth()

  const [stats, setStats] = useState<PartnerDashboardStats | null>(null)
  const [activeCoupon, setActiveCoupon] = useState<ActiveCoupon | null>(null)
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    if (!partner) return
    setLoading(true)
    setError(null)

    const partnerId = partner.id
    const { fromDate, toDate } = currentMonthRange()
    const now = new Date().toISOString()

    try {
      // Lancer les 3 requêtes en parallèle
      const [statsRes, couponRes, ordersRes] = await Promise.all([
        // a) Stats agrégées (RPC)
        supabase.rpc('partner_dashboard_stats', {
          p_partner_id: partnerId,
          p_from_date: fromDate,
          p_to_date: toDate,
        }),

        // b) Coupon PARTNER actif (type=PARTNER, is_active = true, valid_from <= now <= valid_until)
        supabase
          .from('coupons')
          .select('id, code, type, partner_commission_pct, member_discount_pct, user_discount_pct, valid_from, valid_until, max_usage_count, current_usage_count')
          .eq('partner_id', partnerId)
          .eq('type', 'PARTNER')
          .eq('is_active', true)
          .lte('valid_from', now)
          .gte('valid_until', now)
          .order('created_at', { ascending: true })
          .limit(1),

        // c) 10 dernières commandes
        supabase
          .from('orders')
          .select('id, created_at, status, total_price, partner_commission_amount, member_discount_amount, coupon:coupons!coupon_id(code), racket_owner_name, profile:profiles!fk_orders_user(full_name, email)')
          .eq('partner_id', partnerId)
          .order('created_at', { ascending: false })
          .limit(10),
      ])

      // Traiter les stats
      if (statsRes.error) throw new Error(`Stats: ${statsRes.error.message}`)
      const rawStats = Array.isArray(statsRes.data) ? statsRes.data[0] : statsRes.data
      setStats(rawStats ? {
        orders_count: Number(rawStats.orders_count) || 0,
        in_progress_count: Number(rawStats.in_progress_count) || 0,
        commission_sum: Number(rawStats.commission_sum) || 0,
        discount_sum: Number(rawStats.discount_sum) || 0,
      } : { orders_count: 0, in_progress_count: 0, commission_sum: 0, discount_sum: 0 })

      // Traiter le coupon actif
      if (couponRes.error) throw new Error(`Coupon: ${couponRes.error.message}`)
      setActiveCoupon(couponRes.data?.[0] ?? null)

      // Traiter les commandes récentes
      if (ordersRes.error) throw new Error(`Orders: ${ordersRes.error.message}`)
      const mapped: RecentOrder[] = (ordersRes.data ?? []).map((o: Record<string, unknown>) => ({
        id: o.id as string,
        created_at: o.created_at as string,
        status: o.status as string,
        client_name: (o.racket_owner_name as string) || (o.profile as Record<string, unknown> | null)?.full_name as string || (o.profile as Record<string, unknown> | null)?.email as string || '—',
        total_price: Number(o.total_price) || 0,
        partner_commission_amount: Number(o.partner_commission_amount) || 0,
        member_discount_amount: Number(o.member_discount_amount) || 0,
        coupon_code: ((o.coupon as Record<string, unknown> | null)?.code as string) ?? null,
      }))
      setRecentOrders(mapped)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue'
      console.error('[usePartnerDashboardData]', message)
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [partner])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { stats, activeCoupon, recentOrders, loading, error, retry: fetchData }
}
